<!--
    Use this to sync the width or height of physically adjacent elements when a table isn't appropriate.

    Pass a `selector` to decide which elements get resized.

    Pass an `anchor` prop to declare the side along which the items are already aligned.
    Usually that's "left" to match widths and "top" to match heights. Non-adjacent elements will be skipped.
    You can set the tolerance of the alignment with the `tolerance` prop if necessary.

    E.g. to make these labels the same size (and align the inputs):
    <div>
        <cp-dimension-sync selector=".label" anchor"left">
            <label class="label">
                <span>Short label</span>
            </label>
        </cp-dimension-sync>
        <input />
        <br />
        <cp-dimension-sync selector=".label" anchor"left">
            <label class="label">
                <span>And a long label before the input</span>
            </label>
        </cp-dimension-sync>
        <input />
    </div>
-->

<script>
    let debounceTimeout = NaN;
    const instances = [];

    // I haven't benchmarked anything, but it really feels like attaching a single debounced event handler
    // for all instances of this thing (there could be quite a few) is safer than each attaching its own.
    function handleResizeForAll() {
        clearTimeout(debounceTimeout);
        debounceTimeout = setTimeout(() => {
            instances.forEach(instance => instance.handleResize());
        }, 10);
    }

    export default {
        props: {
            anchor: {type: String, required: true}, // "left" or "right" or "top" or "bottom"
            selector: {type: String, required: true},
            tolerance: {type: Number, default: 1},
        },

        computed: {
            dimension() {
                return {
                    left: 'width',
                    right: 'width',
                    top: 'height',
                    bottom: 'height',
                }[this.anchor];
            },
        },

        methods: {
            handleResize() {
                const relatedElements = this.findMatchingElementsInAlignment();
                relatedElements.forEach(element => element.style[this.dimension] = '');

                if (relatedElements.length === 1) {
                    return;
                }

                const sizes = relatedElements.map(element => element.getBoundingClientRect()[this.dimension]);
                const maximum = Math.max.apply(Math, sizes);

                relatedElements.forEach(element => {
                    element.style[this.dimension] = `${maximum}px`;
                });
            },

            findMatchingElementsInAlignment() {
                const anchorLength = this.$el.getBoundingClientRect()[this.anchor];
                const matchingElements = [...document.querySelectorAll(this.selector)];

                return matchingElements.filter((element) => {
                    return Math.abs(anchorLength - element.getBoundingClientRect()[this.anchor]) < this.tolerance;
                });
            },
        },

        render() {
            if (this.$slots.default.length !== 1) {
                throw new Error('DimensionSync requires exactly one child, the element to resize.');
            }

            return this.$slots.default[0];
        },

        async mounted() {
            const isFirstInstance = instances.length === 0;
            instances.push(this);
            this.handleResize();
            if (isFirstInstance) {
                addEventListener('resize', handleResizeForAll);
            }
        },

        beforeDestroy() {
            const instanceIndex = instances.indexOf(this);
            instances.splice(instanceIndex, 1);
            const wasLastInstance = instances.length === 0;
            if (wasLastInstance) {
                removeEventListener('resize', handleResizeForAll);
            }
        },
    };
</script>
